import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  ArrowLeft, 
  Loader2, 
  AlertCircle,
  CheckCircle,
  MessageCircle,
  FileText,
  Building
} from 'lucide-react';
import { useAuthToken } from '../../components/AuthTokenProvider';
import { sowService } from '../../services/sowService';
import { sowConversationService } from '../../services/sowConversationService';
import { SOW } from '../../models/sow';
import { RefineQuestionComponent } from '../../components/RefineQuestionComponent';
import { ImageGallery } from '../../components/ImageGallery';

interface QuestionnaireRefinementPageProps {}

interface Questionnaire {
  id: string;
  name: string;
  category: string;
  status: 'pending' | 'in_progress' | 'completed';
  questions: any[];
  answers: any[];
  completedAt: string | null;
}

export const QuestionnaireRefinementPage: React.FC<QuestionnaireRefinementPageProps> = () => {
  const { sowId, questionnaireId } = useParams<{ 
    sowId: string; 
    questionnaireId: string; 
  }>();
  const navigate = useNavigate();
  const { tokenSet, isLoading: tokenLoading } = useAuthToken();
  
  // Core data
  const [sow, setSOW] = useState<SOW | null>(null);
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<any>(null);
  
  // Batch state
  const [questionBatch, setQuestionBatch] = useState<any>(null);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null);
  const [batchAnswers, setBatchAnswers] = useState<Array<{ question: any; answer: string }>>([]);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [imageRefreshTrigger, setImageRefreshTrigger] = useState(0);

  useEffect(() => {
    if (sowId && questionnaireId && tokenSet && !tokenLoading) {
      loadQuestionnaireData();
    }
  }, [sowId, questionnaireId, tokenSet, tokenLoading]);

  const loadQuestionnaireData = async () => {
    if (!sowId || !questionnaireId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Load SOW and questionnaires
      const questionnairesData = await sowConversationService.getQuestionnaires(sowId);
      setSOW(questionnairesData as any); // Type assertion for now
      
      const targetQuestionnaire = questionnairesData.questionnaires.find(
        (q: Questionnaire) => q.id === questionnaireId
      );
      
      if (!targetQuestionnaire) {
        setError('Questionnaire not found');
        return;
      }
      
      setQuestionnaire(targetQuestionnaire);
      
      if (targetQuestionnaire.status === 'pending') {
        // Start the questionnaire
        const startResponse = await sowConversationService.startQuestionnaire(sowId, questionnaireId);
        
        if (startResponse.questionBatch) {
          setQuestionBatch(startResponse.questionBatch);
          setBatchProgress(startResponse.batchProgress);
          setCurrentQuestion(startResponse.outstandingQuestion);
        }
      } else if (targetQuestionnaire.status === 'in_progress') {
        // Load existing questions from questionnaire data
        if (targetQuestionnaire.questions && targetQuestionnaire.questions.length > 0) {
          // Find the first unanswered question
          const answeredQuestionIds = new Set(targetQuestionnaire.answers.map((a: any) => a.questionId));
          const nextQuestion = targetQuestionnaire.questions.find((q: any) => !answeredQuestionIds.has(q.id));
          
          if (nextQuestion) {
            // Format the question for RefineQuestionComponent
            // Auto-detect if question needs a picture based on text content
            const questionText = nextQuestion.text || '';
            const needsPicture = nextQuestion.needPicture || 
                               nextQuestion.NeedPicture || 
                               questionText.toLowerCase().includes('show me') ||
                               questionText.toLowerCase().includes('photo') ||
                               questionText.toLowerCase().includes('picture') ||
                               questionText.toLowerCase().includes('image') ||
                               false;
                               
            const formattedQuestion = {
              id: nextQuestion.id,
              QuestionText: questionText,
              Options: nextQuestion.options,
              NeedPicture: needsPicture,
              AllowMultiple: nextQuestion.allowMultiple || nextQuestion.AllowMultiple || false
            };
            setCurrentQuestion(formattedQuestion);
            
            // Set progress
            setBatchProgress({
              current: targetQuestionnaire.answers.length + 1,
              total: targetQuestionnaire.questions.length
            });
          }
        }
      } else if (targetQuestionnaire.status === 'completed') {
        setIsComplete(true);
        setTimeout(() => {
          navigate(`/sow/${sowId}`);
        }, 2000);
      }
      
    } catch (error) {
      console.error('Error loading questionnaire data:', error);
      setError('Failed to load questionnaire. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async (userResponse: string) => {
    if (!questionnaire) {
      setError('Questionnaire data not found');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // For 'in_progress' questionnaires, handle local question progression
      if (questionnaire.status === 'in_progress') {
        // Add current answer to the batch
        if (currentQuestion) {
          const newAnswer = { 
            question: { 
              id: currentQuestion.id || `q_${Date.now()}`, 
              ...currentQuestion 
            }, 
            answer: userResponse 
          };
          const updatedAnswers = [...batchAnswers, newAnswer];
          setBatchAnswers(updatedAnswers);
          
          // Find next unanswered question
          const allAnsweredIds = new Set([
            ...questionnaire.answers.map((a: any) => a.questionId),
            ...updatedAnswers.map(a => a.question.id)
          ]);
          
          const nextQuestion = questionnaire.questions.find((q: any) => !allAnsweredIds.has(q.id));
          
          if (nextQuestion) {
            // Move to next question
            // Auto-detect if question needs a picture based on text content
            const questionText = nextQuestion.text || '';
            const needsPicture = nextQuestion.needPicture || 
                               nextQuestion.NeedPicture || 
                               questionText.toLowerCase().includes('show me') ||
                               questionText.toLowerCase().includes('photo') ||
                               questionText.toLowerCase().includes('picture') ||
                               questionText.toLowerCase().includes('image') ||
                               false;
                               
            const formattedNextQuestion = {
              id: nextQuestion.id,
              QuestionText: questionText,
              Options: nextQuestion.options,
              NeedPicture: needsPicture,
              AllowMultiple: nextQuestion.allowMultiple || nextQuestion.AllowMultiple || false
            };
            setCurrentQuestion(formattedNextQuestion);
            
            // Update progress
            const totalAnswered = questionnaire.answers.length + updatedAnswers.length;
            setBatchProgress({
              current: totalAnswered + 1,
              total: questionnaire.questions.length
            });
            
            setSubmitting(false);
            return;
          }
          
          // No more questions - complete the questionnaire
          const formattedAnswers = updatedAnswers.map(answer => ({
            questionId: answer.question.id,
            answer: answer.answer
          }));
          
          const response = await sowConversationService.completeQuestionnaire(
            sowId!,
            questionnaireId!,
            formattedAnswers
          );
          
          // Check if there are more questionnaires to complete
          const questionnairesData = await sowConversationService.getQuestionnaires(sowId!);
          const pendingQuestionnaires = questionnairesData.questionnaires.filter((q: any) => 
            q.status === 'pending' && q.id !== questionnaireId // Exclude current questionnaire
          );
          
          if (pendingQuestionnaires.length > 0) {
            // Navigate to the next pending questionnaire
            const nextQuestionnaire = pendingQuestionnaires[0];
            navigate(`/sow/${sowId}/questionnaire/${nextQuestionnaire.id}`);
            setSubmitting(false);
            return;
          }
          
          // Check if all work items are completed
          if (response.allWorkItemsCompleted) {
            setIsComplete(true);
            setTimeout(() => {
              navigate(`/sow/${sowId}`);
            }, 3000);
          } else {
            // No more questionnaires, redirect back to overview
            navigate(`/sow/${sowId}`);
          }
          
          setSubmitting(false);
          return;
        }
      }
      
      // Handle batch-based questionnaires (from API)
      // Store the current answer
      if (currentQuestion) {
        const newAnswer = { question: currentQuestion, answer: userResponse };
        const updatedAnswers = [...batchAnswers, newAnswer];
        setBatchAnswers(updatedAnswers);
      }
      
      // Check if we have more questions in the current batch
      if (questionBatch && batchProgress && batchProgress.current < batchProgress.total) {
        // Move to next question in the batch locally
        const nextQuestionIndex = batchProgress.current; // current is 1-based, array is 0-based
        const nextQuestion = questionBatch.questions[nextQuestionIndex];
        
        setCurrentQuestion(nextQuestion);
        setBatchProgress({ current: batchProgress.current + 1, total: batchProgress.total });
        
        setSubmitting(false);
        return;
      }
      
      // If we've completed the batch/questionnaire, send all answers to backend
      const allAnswers = [...batchAnswers, { question: currentQuestion, answer: userResponse }];
      const formattedAnswers = allAnswers.map(answer => ({
        questionId: answer.question.id || `q_${Date.now()}`,
        answer: answer.answer
      }));
      
      const response = await sowConversationService.completeQuestionnaire(
        sowId!,
        questionnaireId!,
        formattedAnswers
      );
      
      // Check if there are more questionnaires to complete
      const questionnairesData = await sowConversationService.getQuestionnaires(sowId!);
      const pendingQuestionnaires = questionnairesData.questionnaires.filter((q: any) => 
        q.status === 'pending' && q.id !== questionnaireId // Exclude current questionnaire
      );
      
      if (pendingQuestionnaires.length > 0) {
        // Navigate to the next pending questionnaire
        const nextQuestionnaire = pendingQuestionnaires[0];
        navigate(`/sow/${sowId}/questionnaire/${nextQuestionnaire.id}`);
        return;
      }
      
      // Check if all work items are completed
      if (response.allWorkItemsCompleted) {
        setIsComplete(true);
        setTimeout(() => {
          navigate(`/sow/${sowId}`);
        }, 3000);
      } else {
        // No more questionnaires, redirect back to overview
        navigate(`/sow/${sowId}`);
      }
      
    } catch (error) {
      console.error('Error submitting response:', error);
      setError('Failed to submit response. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToOverview = () => {
    navigate(`/sow/${sowId}`);
  };

  const handleImageUpload = async (file: File): Promise<string> => {
    if (!sowId || !questionnaireId) {
      throw new Error('SOW ID or questionnaire ID not found');
    }
    
    try {
      // Upload image using SOW service 
      const imageId = await sowService.uploadImageForAnalysis(sowId, file);
      
      // CRUCIAL: Also analyze and store the image in SOW's image registry
      await sowService.analyzeSOWImage(sowId, imageId, sow?.scopeDefinition?.projectType || 'Kitchen', {
        type: 'work_item_questionnaire',
        questionnaireId: questionnaireId,
        questionId: currentQuestion?.id
      });
      
      console.log('Image uploaded and analyzed with ID:', imageId);
      return imageId;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleImageUploaded = () => {
    // Trigger image gallery refresh
    setImageRefreshTrigger(prev => prev + 1);
  };

  const handleImageAssessment = async (imageId: string, questionId: string, attemptNumber: number) => {
    if (!sowId || !currentQuestion) {
      throw new Error('Missing SOW ID or current question');
    }

    try {
      const assessment = await sowService.assessImageQuality(
        sowId, 
        imageId, 
        questionId, 
        currentQuestion.QuestionText, 
        attemptNumber
      );
      
      console.log('Image assessment result:', assessment);
      return assessment;
    } catch (error) {
      console.error('Error assessing image:', error);
      // Return a fallback assessment to avoid blocking the user
      return {
        status: 'accepted',
        confidence: 0.6,
        feedback: 'Unable to assess image quality, but proceeding with estimate.',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  // Loading state
  if (loading || tokenLoading || !tokenSet) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <div className="text-lg">Loading questionnaire...</div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Unable to Load Questionnaire</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-x-3">
              <Button onClick={handleBackToOverview}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Overview
              </Button>
              <Button onClick={loadQuestionnaireData} variant="outline">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Completion state
  if (isComplete) {
    return (
      <div className="container mx-auto p-6">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Questionnaire Complete!</h3>
            <p className="text-gray-600 mb-4">
              Your responses have been saved and work items updated.
            </p>
            <div className="flex items-center justify-center mb-6">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              <span>Redirecting back to overview...</span>
            </div>
            <Button onClick={handleBackToOverview} variant="outline">
              Return to Overview
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={handleBackToOverview}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Overview
        </Button>
        
        <div className="flex items-center gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold">Complete Questionnaire</h1>
            <div className="flex items-center gap-2 text-gray-600">
              <Building className="h-4 w-4" />
              <span>{(sow as any)?.scopeDefinition?.projectType}</span>
              <span>•</span>
              <FileText className="h-4 w-4" />
              <span>{(sow as any)?.address}</span>
            </div>
          </div>
        </div>

        {/* Questionnaire Info */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-1">{questionnaire?.name}</h3>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="outline" className="text-xs">
                  {questionnaire?.category}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {questionnaire?.status === 'pending' ? 'Starting...' :
                   questionnaire?.status === 'in_progress' ? 'In Progress' :
                   'Completed'}
                </Badge>
                {batchProgress && (
                  <Badge variant="outline" className="text-xs">
                    Question {batchProgress.current} of {batchProgress.total}
                  </Badge>
                )}
              </div>
              {/* Progress Bar */}
              {batchProgress && (
                <div className="space-y-1">
                  <div className="text-xs text-blue-700 font-medium">
                    Progress: {batchProgress.current} of {batchProgress.total} questions
                  </div>
                  <Progress 
                    value={((batchProgress.current - 1) / batchProgress.total) * 100} 
                    max={100}
                    className="h-2 bg-blue-100"
                  />
                </div>
              )}
            </div>
            <MessageCircle className="h-6 w-6 text-blue-600 ml-4" />
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {/* Current Question */}
          {currentQuestion && (
            <RefineQuestionComponent
              question={currentQuestion}
              onContinue={handleContinue}
              onImageUpload={handleImageUpload}
              onImageUploaded={handleImageUploaded}
              onImageAssessment={handleImageAssessment}
              isSubmitting={submitting}
            />
          )}
        </div>

        {/* Sidebar - Image Gallery */}
        <div className="lg:col-span-1">
          <ImageGallery 
            sowId={sowId!} 
            className="sticky top-6"
            refreshTrigger={imageRefreshTrigger}
          />
        </div>
      </div>
    </div>
  );
};